/**
 * DXF Parser Service
 *
 * Service layer for DXF to CSV conversion API communication
 * Handles DXF file selection and conversion to CSV format
 */

import axios from 'axios'
import { createAuthenticatedClient } from './api/apiClient'
import type { ZoneRegionsResult } from '../map-editor-features/zone-auto-import/model/types'
import type { BuildingLabel } from '../map-editor-features/dong-auto-import'
import type { DrawingObject } from '../map-editor-features/drawing-check/lib/compareDrawing'

// Use centralized apiClient with extended timeout for DXF conversion.
// CCTV 이름/구역 추출은 대형 도면 PDF 에서 오래 걸릴 수 있어 10분으로 상향
// (frontend nginx proxy_read_timeout 600s 와 backend 파이썬 타임아웃과 정렬).
const api = createAuthenticatedClient({
  timeout: 1200000, // 20 minutes for DXF/PDF processing (can be slow)
})

/**
 * Sample Shape Information for layer preview
 */
export interface SampleShape {
  type: string // 도형 타입 (polygon)
  coordinates: number[][] // 정규화된 좌표 (0-100)
  originalArea: number // 원본 면적 (m²)
}

/**
 * Layer Information
 */
export interface LayerInfo {
  name: string // 레이어/블록 이름
  count: number // 사용 횟수
  sampleArea: number // 샘플 면적 (m²)
  suggestedType: string // 추정 타입
  selected: boolean // 선택 여부
  sampleShape?: SampleShape // 도형 미리보기 데이터 (선택사항)
  // 변환 파이프라인이 추출하지 못하는 엔티티 타입별 개수 (SOLID/구식 POLYLINE 등)
  unsupportedTypes?: Record<string, number>
  // 비-CCTV 타입으로 추정된 레이어에 얹힌 CCTV 계열 블록명→개수 (오분류 경고)
  cctvBlocks?: Record<string, number>
}

/**
 * Selected Layer with Type Information
 */
export interface SelectedLayerInfo {
  name: string // 레이어 이름
  suggestedType: string // 추정된 주차 타입
}

/**
 * Layer Analysis Request
 */
export interface AnalyzeLayersRequest {
  projectId: number
  mapId?: number
  dxfFilename: string
}

/**
 * Layer Analysis Response
 */
export interface AnalyzeLayersResponse {
  success: boolean
  message: string
  data?: {
    layers: LayerInfo[]
    totalLayers: number
  }
}

/**
 * DXF Conversion Request
 */
export interface ConvertDxfRequest {
  projectId: number
  mapId?: number
  dxfFilename: string
  selectedLayers?: SelectedLayerInfo[] // 선택된 레이어 정보 (이름 + 타입)
  /**
   * CCTV ↔ IP 라벨 매칭 최대 거리(m). 0 또는 미지정 시 백엔드 기본값(2m).
   * 도면이 라벨을 멀리 박아두는 경우 5~10 으로 조정.
   */
  cctvIpRadiusM?: number
}

/**
 * 의심 주차 폴리곤 1개 (비파괴 형상 진단)
 */
export interface ParkingQualityItem {
  layer: string
  center: [number, number] // [x, y] (m)
  rectangularity?: number // 폴리곤면적/최소회전사각형면적 (1.0=완전 직사각형)
  max_angle_dev_deg?: number // 코너 90° 최대 편차
  reasons: string[]
}

/**
 * 주차 폴리곤 형상 품질 진단 결과 (추출은 변경하지 않음)
 */
export interface ParkingQualityReport {
  total: number
  suspicious: number
  items: ParkingQualityItem[]
}

/**
 * DXF Conversion Response
 */
export interface ConvertDxfResponse {
  success: boolean
  message: string
  data?: {
    filename: string // Generated CSV filename with timestamp
    status: 'completed'
    sizeBytes?: number
    lineCount?: number
    /** Source meters minus this offset equals the generated CSV coordinates. */
    coordinateOffset?: { x: number; y: number }
    parkingQualityReport?: ParkingQualityReport
    /**
     * DXF 라벨에서 추출한 CCTV IP 목록 (meter 좌표). 프로젝트에 cctv_ip_prefix 가
     * 설정되어 있을 때만 채워진다. 없으면 undefined. 자동 IP 배선에 사용.
     */
    cctvIps?: { ip: string; x: number; y: number }[]
  }
}

/**
 * DXF Parser Service Class
 *
 * Provides methods for:
 * - Analyzing DXF file layers
 * - Converting DXF files to CSV format via Python script
 */
export class DxfParserService {
  /**
   * Analyze DXF file layers
   *
   * @param projectId - Site project ID
   * @param dxfFilename - DXF filename in server storage (e.g., "central.dxf")
   * @returns Layer analysis response with layer information
   */
  static async analyzeLayers(projectId: number, dxfFilename: string, mapId?: number): Promise<AnalyzeLayersResponse> {
    try {
      const endpoint = `/v0.1/dxf-parser/analyze-layers`

      const response = await api.post<AnalyzeLayersResponse>(endpoint, {
        projectId,
        mapId: mapId || 0,
        dxfFilename,
      })

      return response.data
    } catch (error) {
      console.error('[DxfParserService] Layer analysis failed:', error)

      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'DXF 레이어 분석 실패')
      }

      throw new Error('DXF 레이어 분석 중 오류가 발생했습니다')
    }
  }

  /**
   * Convert DXF file to CSV
   *
   * @param projectId - Site project ID
   * @param dxfFilename - DXF filename in server storage (e.g., "central.dxf")
   * @param selectedLayers - Optional array of selected layer info with types
   * @returns Conversion response with generated CSV filename
   */
  static async convertDxfToCsv(
    projectId: number,
    dxfFilename: string,
    selectedLayers?: SelectedLayerInfo[],
    mapId?: number,
    cctvIpRadiusM?: number
  ): Promise<ConvertDxfResponse> {
    try {
      const endpoint = `/v0.1/dxf-parser/convert`

      const response = await api.post<ConvertDxfResponse>(endpoint, {
        projectId,
        mapId: mapId || 0,
        dxfFilename,
        selectedLayers,
        ...(cctvIpRadiusM && cctvIpRadiusM > 0 ? { cctvIpRadiusM } : {}),
      })

      return response.data
    } catch (error) {
      console.error('[DxfParserService] Conversion failed:', error)

      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data?.message || 'DXF 변환 실패')
      }

      throw new Error('DXF 변환 중 오류가 발생했습니다')
    }
  }

  /**
   * 서버에 저장된 CCTV IP 사이드카(<csv>.cctvips.json)를 조회한다.
   * 변환 시 백엔드가 생성·보존한 파일을 수동 📷 버튼이 직접 불러올 때 사용.
   *
   * @param projectId - 현장(site) 프로젝트 ID (URL /project/{id})
   * @param csvFilename - 선택: 특정 CSV 의 사이드카를 지정. 없으면 최신 사용.
   * @returns CCTV IP 매칭 항목 배열 ({ip,x,y})
   */
  static async fetchServerCctvIps(
    projectId: number,
    csvFilename?: string
  ): Promise<{ ip: string; x: number; y: number; handle?: string }[]> {
    const endpoint = `/v0.1/dxf-parser/cctv-ips`
    const response = await api.get(endpoint, {
      params: { projectId, ...(csvFilename ? { csvFilename } : {}) },
    })
    return response.data?.items ?? []
  }

  /**
   * 도면 대조 — 층 CSV 를 만든 원본 DXF 에서 기본 객체(주차면·CCTV·비상벨·기둥)를 다시 뽑는다.
   * 서버가 도면을 다시 변환하므로 큰 도면은 1~2분 걸린다. 좌표는 층 CSV 좌표(미터).
   */
  static async fetchDrawingObjects(
    projectId: number,
    csvFilename: string
  ): Promise<{ dxfFilename: string; objects: DrawingObject[] }> {
    try {
      const response = await api.get('/v0.1/dxf-parser/drawing-objects', { params: { projectId, csvFilename } })
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      throw new Error('도면을 다시 읽지 못했습니다')
    }
  }

  /**
   * 서버에 저장된 동/건물 라벨 사이드카(<csv>.buildings.json)를 조회한다.
   * extract_building_labels.py 가 DXF 전용 레이어(큰 글자)에서 추출하며, 좌표는
   * CCTV IP 사이드카와 같은 좌표계(meter-offset)라 같은 변환을 적용할 수 있다.
   *
   * @param projectId - 현장(site) 프로젝트 ID
   * @param csvFilename - 선택: 특정 CSV 의 사이드카 지정. 없으면 최신 사용.
   * @returns 동/건물 라벨 배열 ({name,x,y})
   */
  static async fetchServerBuildingLabels(projectId: number, csvFilename?: string): Promise<BuildingLabel[]> {
    const endpoint = `/v0.1/dxf-parser/building-labels`
    const response = await api.get(endpoint, {
      params: { projectId, ...(csvFilename ? { csvFilename } : {}) },
    })
    return response.data?.items ?? []
  }

  /**
   * 도면 PDF 를 업로드해 CCTV 이름↔IP 매핑을 추출한다 (🏷️ CCTV 이름 버튼).
   * IP prefix 는 현장의 'CCTV IP 앞자리' 설정에서 백엔드가 읽는다.
   *
   * @param projectId - 현장(site) 프로젝트 ID (URL /project/{id})
   * @param file - 텍스트가 살아있는 도면 PDF
   * @param prefix - 배치된 카메라 IP 에서 유추한 앞 두 옥텟(예: 172.19).
   *                 비우면 백엔드가 현장의 'CCTV IP 앞자리' 설정으로 폴백.
   * @returns 매핑 결과 (items: [{ip, name}] + ipLabels: 첫 페이지 IP 라벨 좌표
   *          [{ip,x,y}] (PDF pt, Y-UP) + 진단 정보). ipLabels 는 카메라 점군
   *          정렬로 ipAddress 를 채우는 입력이라 이름 매칭과 무관하게 전부 온다.
   */
  static async extractCctvNames(
    projectId: number,
    file: File,
    prefix?: string
  ): Promise<{
    prefix: string
    totalNames: number
    totalIps: number
    matched: number
    unmatchedNames: string[]
    unmatchedIps: string[]
    conflicts: string[]
    items: { ip: string; name: string }[]
    ipLabels: { ip: string; x: number; y: number }[]
  }> {
    const fd = new FormData()
    fd.append('projectId', String(projectId))
    fd.append('file', file)
    if (prefix) fd.append('prefix', prefix)
    const response = await api.post(`/v0.1/dxf-parser/cctv-names`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    if (!response.data?.success || !response.data?.mapping) {
      throw new Error(response.data?.message || 'PDF 파싱에 실패했습니다')
    }
    return response.data.mapping
  }

  /**
   * 도면 PDF 를 업로드해 빨간 구역선 폴리곤 + CCTV 이름 라벨 좌표를 추출한다
   * (구역 가져오기 버튼). 좌표는 PDF pt (Y-UP, 원점 좌하단) 기준 — 에디터가
   * 배치된 카메라와의 이름 매칭으로 좌표 변환을 추정한다.
   *
   * @param projectId - 현장(site) 프로젝트 ID (URL /project/{id})
   * @param file - 텍스트가 살아있는 도면 PDF
   * @returns 구역 추출 결과 (page/zones/nameLabels/stats)
   */
  static async extractZoneRegions(projectId: number, file: File): Promise<ZoneRegionsResult> {
    const fd = new FormData()
    fd.append('projectId', String(projectId))
    fd.append('file', file)
    const response = await api.post(`/v0.1/dxf-parser/zone-regions`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    if (!response.data?.success || !response.data?.regions) {
      throw new Error(response.data?.message || 'PDF 파싱에 실패했습니다')
    }
    return response.data.regions
  }
}
